import React, { useState, useCallback, useMemo } from "react";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";

import {
    DEFAULT_ZOOM,
    MAX_ZOOM,
    MIN_ZOOM,
    ZoomContext,
    ZoomContextType,
    ZoomState
} from "./ZoomContext";

export const ZoomProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [zoomState, setZoomState] = useState<ZoomState>({
        zoomLevel: DEFAULT_ZOOM,
        isLocked: false,
        x: 0,
        y: 0
    });

    const zoomIn = useCallback(() => {
        setZoomState((prev) => {
            let newZoom = prev.zoomLevel;
            if (newZoom < 1) {
                // Zoom in by 10% when below 100%
                newZoom = Math.round((newZoom + 0.1) * 10) / 10;
            } else {
                // Zoom in proportionally when above 100%
                newZoom = Math.round(newZoom * 1.075 * 10) / 10;
            }

            // Ensure we don't exceed max zoom
            newZoom = Math.min(newZoom, MAX_ZOOM);

            // Snap to 100% if we're close
            if (newZoom < 1 && newZoom > 0.95) {
                newZoom = 1;
            }

            return { ...prev, zoomLevel: newZoom };
        });
    }, []);

    const zoomOut = useCallback(() => {
        setZoomState((prev) => {
            let newZoom = prev.zoomLevel;
            if (newZoom <= 1) {
                // Zoom out by 10% when at or below 100%
                newZoom = Math.round((newZoom - 0.1) * 10) / 10;
            } else {
                // Zoom out proportionally when above 100%
                newZoom = Math.round(newZoom * 0.9 * 10) / 10;
            }

            // Ensure we don't go below min zoom
            newZoom = Math.max(newZoom, MIN_ZOOM);

            // Snap to 100% if we're close
            if (newZoom > 1 && newZoom < 1.05) {
                newZoom = 1;
            }

            return { ...prev, zoomLevel: newZoom };
        });
    }, []);

    const resetZoom = useCallback(() => {
        setZoomState((prev) => ({ ...prev, zoomLevel: DEFAULT_ZOOM, x: 0, y: 0 }));
    }, []);

    const setZoom = useCallback((zoom: number, x?: number, y?: number) => {
        const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
        setZoomState((prev) => ({
            ...prev,
            zoomLevel: clampedZoom,
            x: x !== undefined ? x : prev.x,
            y: y !== undefined ? y : prev.y
        }));
    }, []);

    const toggleLock = useCallback(() => {
        setZoomState((prev) => ({ ...prev, isLocked: !prev.isLocked }));
    }, []);

    const setPosition = useCallback((x: number, y: number) => {
        setZoomState((prev) => ({ ...prev, x, y }));
    }, []);

    const pan = useCallback((deltaX: number, deltaY: number) => {
        setZoomState((prev) => ({ ...prev, x: prev.x + deltaX, y: prev.y + deltaY }));
    }, []);

    // Add keyboard event listener for zoom shortcuts
    // Keyboard shortcuts for zoom
    useKeyboardShortcut({ key: ["=", "+"], handler: zoomIn, requireMeta: true });
    useKeyboardShortcut({ key: "-", handler: zoomOut, requireMeta: true });
    useKeyboardShortcut({ key: "0", handler: resetZoom, requireMeta: true });

    const contextValue = useMemo<ZoomContextType>(
        () => ({
            zoomLevel: zoomState.zoomLevel,
            isLocked: zoomState.isLocked,
            x: zoomState.x,
            y: zoomState.y,
            zoomIn,
            zoomOut,
            resetZoom,
            setZoom,
            toggleLock,
            setPosition,
            pan
        }),
        [zoomState, zoomIn, zoomOut, resetZoom, setZoom, toggleLock, setPosition, pan]
    );

    return <ZoomContext.Provider value={contextValue}>{children}</ZoomContext.Provider>;
};
